/**
 * SSE Service - Default Implementation
 *
 * Manages SSE connection lifecycle using native EventSource.
 * Reconnects automatically when auth state changes.
 * Uses session cookie for authentication (browser sends automatically).
 *
 * Uses a single onmessage handler to catch all events.
 * Server embeds event type in data payload: { type: "...", data: {...} }
 * This simplifies reconnection (only one listener to manage).
 */

import { inject, effect } from "../../di";
import type { IWritableSseState, ISseService } from "./types";
import type { IAuthState } from "../auth/types";

const __debug: boolean = false;

export class SseService implements ISseService {
  @inject<IWritableSseState>("writableSseState")
  private sseState!: IWritableSseState;

  @inject<IAuthState>("authState")
  private authState!: IAuthState;

  private eventSource: EventSource | null = null;
  private authEffectCleanup: (() => void) | null = null;

  /**
   * Internal listener registry.
   * Maps event type → Set of handlers for that event.
   */
  private listeners: Map<string, Set<(data: unknown) => void>> = new Map();

  private lastHeartbeatTime: number = 0;
  private healthCheckInterval: ReturnType<typeof setInterval> | null = null;
  private lastReconnectAttempt: number = 0;

  private static readonly HEARTBEAT_TIMEOUT_MS = 45000; // 3x server interval (15s)
  private static readonly HEALTH_CHECK_INTERVAL_MS = 10000;
  private static readonly RECONNECT_COOLDOWN_MS = 1000;
  private static readonly LAST_EVENT_ID_KEY = "wow-sse-last-event-id";

  initialize(): void {
    this.setupAuthListeners();
    this.setupVisibilityHandlers();
    this.setupOnlineHandler();
    this.connect();
    this.startHealthCheck();
  }

  dispose(): void {
    // Clean up auth state effect
    if (this.authEffectCleanup) {
      this.authEffectCleanup();
      this.authEffectCleanup = null;
    }

    if (this.healthCheckInterval) {
      clearInterval(this.healthCheckInterval);
      this.healthCheckInterval = null;
    }
    this.disconnect();
    this.listeners.clear();
  }

  /**
   * Setup auth state watching using signal effects.
   * Reconnects SSE when user signs in or out.
   */
  private setupAuthListeners(): void {
    // Track previous auth state to detect changes
    let previousUserId: string | undefined;
    let isFirstRun = true;

    this.authEffectCleanup = effect(() => {
      const currentUser = this.authState.currentUser.get();
      const currentUserId = currentUser?.id;

      // Skip reconnection on first run (initial render)
      if (isFirstRun) {
        previousUserId = currentUserId;
        isFirstRun = false;
        return;
      }

      // Only reconnect if user actually changed
      if (currentUserId !== previousUserId) {
        if (__debug === true) {
          console.log("[SSE] Auth state changed, reconnecting", {
            from: previousUserId,
            to: currentUserId,
          });
        }
        previousUserId = currentUserId;
        this.connect();
      }
    });
  }

  private setupVisibilityHandlers(): void {
    if (typeof document === "undefined") return;

    document.addEventListener("visibilitychange", () => {
      if (document.visibilityState === "visible") {
        if (__debug === true) {
          console.log("[SSE] Tab became visible, checking connection");
        }
        this.checkConnectionHealth();
      }
    });

    window.addEventListener("pageshow", (event) => {
      if (event.persisted) {
        if (__debug === true) {
          console.log("[SSE] Page restored from bfcache, reconnecting");
        }
        this.reconnect();
      }
    });
  }

  private setupOnlineHandler(): void {
    if (typeof window === "undefined") return;
    window.addEventListener("online", () => {
      if (__debug === true) {
        console.log("[SSE] Network online, checking connection");
      }
      this.checkConnectionHealth();
    });
  }

  private startHealthCheck(): void {
    if (this.healthCheckInterval) return;
    this.healthCheckInterval = setInterval(() => {
      this.checkConnectionHealth();
    }, SseService.HEALTH_CHECK_INTERVAL_MS);
  }

  private checkConnectionHealth(): void {
    if (this.lastHeartbeatTime === 0) return; // Not yet connected

    const timeSinceLastHeartbeat = Date.now() - this.lastHeartbeatTime;
    if (timeSinceLastHeartbeat > SseService.HEARTBEAT_TIMEOUT_MS) {
      if (__debug === true) {
        console.log(`[SSE] Stale connection (${Math.round(timeSinceLastHeartbeat / 1000)}s), reconnecting`);
      }
      this.reconnect();
    }
  }

  private reconnect(): void {
    const now = Date.now();
    if (now - this.lastReconnectAttempt < SseService.RECONNECT_COOLDOWN_MS) {
      return; // Throttled
    }
    this.lastReconnectAttempt = now;
    this.disconnect();
    setTimeout(() => this.connect(), 100);
  }

  private getStoredLastEventId(): string | null {
    if (typeof sessionStorage === "undefined") return null;
    return sessionStorage.getItem(SseService.LAST_EVENT_ID_KEY);
  }

  private storeLastEventId(eventId: string): void {
    if (typeof sessionStorage === "undefined") return;
    sessionStorage.setItem(SseService.LAST_EVENT_ID_KEY, eventId);
  }

  private getSseUrl(): string {
    let baseUrl: string;
    if (typeof window === "undefined" || window.location.hostname === "localhost") {
      baseUrl = "/sse/";
    } else {
      // Always use main domain SSE server (strip 'my.' prefix if present)
      // This ensures backoffice (my.worldofwow.com) connects to same SSE as profile (worldofwow.com)
      const baseDomain = window.location.hostname.replace(/^my\./, "");
      baseUrl = `https://sse.${baseDomain}/`;
    }

    /**
     * Include lastEventId as URL query param for replay across page navigations.
     *
     * WHY QUERY PARAM INSTEAD OF RELYING ON AUTO-REPLAY:
     * Browser's native EventSource only sends Last-Event-ID header during automatic
     * reconnections (network errors, server disconnects). When a page navigates away
     * (e.g., to Stripe checkout) and back, a NEW EventSource instance is created and
     * the browser has no memory of the previous lastEventId.
     *
     * EventSource doesn't support custom headers, so we can't manually set Last-Event-ID.
     * The workaround is to persist lastEventId in sessionStorage and pass it via URL
     * query parameter, which the server reads to replay missed events.
     *
     * See: /docs/sse-service-prd.md for full documentation.
     */
    const lastEventId = this.getStoredLastEventId();
    if (lastEventId) {
      return `${baseUrl}?lastEventId=${encodeURIComponent(lastEventId)}`;
    }
    return baseUrl;
  }

  private connect(): void {
    this.disconnect();
    this.sseState.status.set("connecting");

    // Connect to SSE via custom domain (bypasses Front Door which doesn't support SSE)
    this.eventSource = new EventSource(this.getSseUrl(), { withCredentials: true });

    this.eventSource.onopen = () => {
      this.sseState.status.set("connected");
      if (__debug === true) {
        console.log("[SSE] Connected");
      }
    };

    this.eventSource.onerror = () => {
      this.sseState.status.set("disconnected");
      if (__debug === true) {
        console.log("[SSE] Disconnected, will auto-reconnect...");
      }
    };

    // Single catch-all handler for ALL events
    // Server sends: data: { type: "event-type", data: {...} }
    this.eventSource.onmessage = (event) => {
      try {
        const { type, data } = JSON.parse(event.data) as { type: string; data: unknown };

        // Store lastEventId for replay across page navigations
        // (browser auto-replay only works within same EventSource instance)
        if (event.lastEventId) {
          this.storeLastEventId(event.lastEventId);
        }

        this.handleEvent(type, data);
      } catch (error) {
        console.error("[SSE] Failed to parse event:", error);
      }
    };
  }

  private disconnect(): void {
    if (this.eventSource) {
      this.eventSource.close();
      this.eventSource = null;
    }
  }

  /**
   * Route event to appropriate handlers based on type.
   * Infrastructure events (connected, heartbeat) handled internally.
   * Feature events forwarded to registered listeners.
   */
  private handleEvent(type: string, data: unknown): void {
    // Track heartbeat for connection health monitoring
    if (type === "heartbeat" || type === "connected") {
      this.lastHeartbeatTime = Date.now();
    }

    // Infrastructure events
    if (type === "connected") {
      const { hasSession } = data as { hasSession?: boolean };
      this.sseState.hasSession.set(hasSession ?? false);
      if (__debug === true) {
        console.log("[SSE] Session:", hasSession ? "authenticated" : "anonymous");
      }
      return;
    }

    if (type === "heartbeat") {
      if (__debug === true) {
        console.log("[SSE] heartbeat");
      }
      return;
    }

    // Feature events - forward to listeners
    this.notifyListeners(type, data);
  }

  /**
   * Subscribe to an SSE event type.
   *
   * @param eventType - The SSE event type (e.g., "live-chat-message")
   * @param handler - Callback receiving the parsed event data
   * @returns Unsubscribe function
   */
  on<T = unknown>(eventType: string, handler: (data: T) => void): () => void {
    if (!this.listeners.has(eventType)) {
      this.listeners.set(eventType, new Set());
    }
    this.listeners.get(eventType)!.add(handler as (data: unknown) => void);

    return () => {
      this.listeners.get(eventType)?.delete(handler as (data: unknown) => void);
    };
  }

  /**
   * Notify all handlers subscribed to an event type.
   */
  private notifyListeners(eventType: string, data: unknown): void {
    const handlers = this.listeners.get(eventType);
    if (handlers) {
      handlers.forEach((handler) => handler(data));
    }
  }
}
